import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DateRangePickerProps {
  id?: string;
  startDate?: string;
  endDate?: string;
  onRangeChange: (startDate: string, endDate: string) => void;
  placeholder?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  id,
  startDate,
  endDate,
  onRangeChange,
  placeholder = 'Seleccionar rango de fechas'
}) => {
  // Función para parsear fecha string (YYYY-MM-DD) a Date en zona horaria local
  const parseLocalDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    startDate ? parseLocalDate(startDate) : null
  );
  const [tempEndDate, setTempEndDate] = useState<Date | null>(
    endDate ? parseLocalDate(endDate) : null
  );
  const [currentMonth1, setCurrentMonth1] = useState(new Date());
  const [currentMonth2, setCurrentMonth2] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
  );
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [isApplied, setIsApplied] = useState(!!startDate && !!endDate);

  useEffect(() => {
    if (startDate) setTempStartDate(parseLocalDate(startDate));
    if (endDate) setTempEndDate(parseLocalDate(endDate));
    // Si vienen ambas fechas desde las props, marcar como aplicado
    if (startDate && endDate) setIsApplied(true);
  }, [startDate, endDate]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const generateCalendarDays = (date: Date) => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(date);
    const days: (Date | null)[] = [];
    
    // Agregar espacios vacíos para los días antes del inicio del mes
    const startDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    // Agregar espacios vacíos para completar la última semana
    const remainingDays = days.length % 7;
    if (remainingDays > 0) {
      for (let i = 0; i < 7 - remainingDays; i++) {
        days.push(null);
      }
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    const newMonth1 = new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() - 1, 1);
    const newMonth2 = new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() - 1, 1);
    setCurrentMonth1(newMonth1);
    setCurrentMonth2(newMonth2);
  };

  const handleNextMonth = () => {
    const newMonth1 = new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() + 1, 1);
    const newMonth2 = new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() + 1, 1);
    setCurrentMonth1(newMonth1);
    setCurrentMonth2(newMonth2);
  };

  const handleDayClick = (date: Date) => {
    setIsApplied(false); // Marcar como no aplicado cuando se cambia la selección
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (date < tempStartDate) {
      setTempStartDate(date);
      setTempEndDate(null);
    } else {
      setTempEndDate(date);
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      // Formatear fechas en formato local YYYY-MM-DD sin conversión a UTC
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      onRangeChange(
        formatLocalDate(tempStartDate),
        formatLocalDate(tempEndDate)
      );
      setIsApplied(true); // Marcar como aplicado
    }
  };

  const handleReset = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setIsApplied(false); // Resetear el estado de aplicado
  };

  const calculateDaysDifference = () => {
    if (!tempStartDate || !tempEndDate) return 0;
    const diffTime = Math.abs(tempEndDate.getTime() - tempStartDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'dd/mm/aaaa';
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isDateInRange = (date: Date) => {
    if (!tempStartDate) return false;
    const compareDate = tempEndDate || hoverDate;
    if (!compareDate) return false;
    
    const start = tempStartDate < compareDate ? tempStartDate : compareDate;
    const end = tempStartDate < compareDate ? compareDate : tempStartDate;
    
    return date >= start && date <= end;
  };

  const isStartDate = (date: Date) => {
    return tempStartDate && date.toDateString() === tempStartDate.toDateString();
  };

  const isEndDate = (date: Date) => {
    return tempEndDate && date.toDateString() === tempEndDate.toDateString();
  };

  const isCurrentMonth = (date: Date, monthDate: Date) => {
    return date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const renderCalendar = (monthDate: Date) => {
    const days = generateCalendarDays(monthDate);
    const weeks = [];
    
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <table className="pb-3 w-max">
        <thead className="mb-2">
          <tr className="flex">
            {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map((day) => (
              <td key={day} className="flex items-center justify-center w-10 h-10">
                <p className="text-sm font-medium text-foreground rounded-full flex items-center justify-center w-full h-full transition-all duration-300 hover:bg-primary/10 hover:text-primary">
                  {day}
                </p>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => (
            <tr key={weekIndex} className="flex">
              {week.map((date, dayIndex) => {
                if (!date) return <td key={dayIndex} className="w-10 h-10" />;
                
                const inRange = isDateInRange(date);
                const isStart = isStartDate(date);
                const isEnd = isEndDate(date);
                const isCurrent = isCurrentMonth(date, monthDate);
                const todayDate = isToday(date);
                
                return (
                  <td
                    key={dayIndex}
                    className={`flex items-center justify-center w-10 h-10 ${
                      inRange ? 'bg-muted' : ''
                    } ${isStart ? 'rounded-l-full' : ''} ${isEnd ? 'rounded-r-full' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleDayClick(date)}
                      onMouseEnter={() => setHoverDate(date)}
                      onMouseLeave={() => setHoverDate(null)}
                      className={`text-sm font-medium rounded-full flex items-center justify-center w-full h-full transition-all duration-300 ${
                        isStart || isEnd
                          ? 'bg-primary text-primary-foreground'
                          : todayDate
                          ? 'text-red-600 font-bold hover:bg-red-50 hover:text-red-700 ring-2 ring-red-600'
                          : isCurrent
                          ? 'text-foreground hover:bg-primary/10 hover:text-primary'
                          : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div id={id} className="w-full max-w-xs lg:max-w-2xl p-4 rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-border">
        <div className="flex items-center gap-8 py-0.5 px-0.5 text-xs font-medium text-foreground">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="text-foreground p-2 rounded-md transition-all duration-500 hover:bg-primary/10 hover:text-primary"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {currentMonth1.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
        <div className="flex items-center gap-8 py-0.5 px-0.5 text-xs font-medium text-foreground">
          {currentMonth2.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          <button
            type="button"
            onClick={handleNextMonth}
            className="text-foreground p-2 rounded-md transition-all duration-500 hover:bg-primary/10 hover:text-primary"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center flex-col lg:flex-row gap-6 border-b border-border justify-between">
        {renderCalendar(currentMonth1)}
        <div className="w-px relative lg:h-80 bg-border"></div>
        {renderCalendar(currentMonth2)}
      </div>

      <div className="py-3 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <div>
            <span className="font-medium text-foreground">Fecha inicio: </span>
            <span className="text-muted-foreground">{formatDate(tempStartDate)}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Fecha fin: </span>
            <span className="text-muted-foreground">{formatDate(tempEndDate)}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Días totales: </span>
            <span className="text-muted-foreground">{tempStartDate && tempEndDate ? calculateDaysDifference() : '-'}</span>
          </div>
        </div>
        
        {/* Estado de aplicación */}
        <div className="mt-3 flex items-center justify-center">
          {tempStartDate && tempEndDate ? (
            isApplied ? (
              <span className="text-sm font-medium text-green-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Fecha aplicada
              </span>
            ) : (
              <span className="text-sm font-medium text-orange-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Fecha pendiente de confirmación
              </span>
            )
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-end pt-3 gap-4">
        <button
          type="button"
          onClick={handleReset}
          className="py-2 px-3 rounded-md border border-border text-xs font-medium text-foreground transition-all duration-500 hover:text-primary hover:bg-primary/10"
        >
          Reiniciar
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={!tempStartDate || !tempEndDate || isApplied}
          className={`py-2 px-3 rounded-md text-xs font-medium transition-all duration-500 ${
            isApplied
              ? 'bg-gray-400 text-gray-100 cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;
